<template>
  <q-page
    :data-test="CypressTarget('EventInformation')"
  >
    <event-information-page-skeleton
      v-if="loading.events"
      :square="true"
      :flat="true"
    />

    <div
      v-else
      class="event-data row"
      style="position: relative;"
    >
      <div :class="isInfoEvent(singleEventInformation) ? 'col-xs-12 col-sm-12 col-md-12' : 'col-xs-12 col-sm-12 col-md-6'">
        <card-event
          :class="{'no-margin': $q.platform.is.mobile}"
          :square="true"
          :flat="true"
          :display="cardDisplay"
          :color="SET_COLOR.primary"
          :event-address="singleEventInformation.address"
          :event-location-name="singleEventInformation.location_name"
          :event-description="singleEventInformation.event_description"
          :event-geo-coordinates="singleEventInformation.geo_position"
          :event-name="singleEventInformation.name"
          :event-image="singleEventInformation.image_poster"
          :event-short-description="singleEventInformation.short_description"
          :event-date="singleEventInformation.sales_start"
          :event-subtitle="singleEventInformation.serie"
          :featured="singleEventInformation.featured"
          :is-info-event="isInfoEvent(singleEventInformation)"
          :event-timezone="singleEventInformation.timezone_converted"
          :show-event-indicative-classification="indicativeClassification.displayOnWeb"
          :event-indicative-classification-image="indicativeClassification.imageURL"
          :event-indicative-classification-description="indicativeClassification.description"
          inline
        />
      </div>
      <div
        v-if="!isInfoEvent(singleEventInformation)"
        class="col-xs-12 col-sm-12 col-md-6"
      >
        <component
          :is="eventTicketStatusComponent.component"
          v-bind="eventTicketStatusComponent.bind"
        />
      </div>
    </div>
  </q-page>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { date } from 'quasar';
import CartTimerResetMixin from 'mixins/modules/ticket/cart/cartTimerReset';
import * as ROUTER_CONSTANTS from 'constants/vueRouter/errors';
import { objectValidator } from 'helpers/utils/objectHelpers';
import { UPDATE_PAGE_DATA } from 'constants/eventBus';
import LayoutHelpers from 'helpers/utils/layout';
import {
  TICKET_EVENT_NAME,
  TICKET_FUTURE_EVENT_NAME,
} from 'constants/vueRouter/ticket';

export default {
  name: 'EventInformationTicket',
  components: {
    CardEvent: () => import('components/card/event'),
    SkeletonEventList: () => import('skeleton/events/List'),
    EventInformationPageSkeleton: () => import('skeleton/pages/events/Information'),
  },
  mixins: [CartTimerResetMixin],
  computed: {
    ...mapGetters('ticketSale', [
      'singleEventInformation',
      'singleEventMode',
      'loading',
    ]),
    ...mapGetters('ticketShoppingCart', [
      'sessionCart',
      'sessionCartLoaded',
      'isCartValid',
    ]),
    eventTicketStatusComponent() {
      if (this.isOnSale && this.singleEventInformation.enable) {
        return {
          component: () => import('activeComponent/ticket/list/event/List'),
          bind: {},
        };
      }
      return {
        component: () => import('components/status/event/component'),
        bind: {
          eventDisabled: !this.singleEventInformation.enable,
          startSaleDate: this.singleEventInformation.sales_start,
          isSalesOpen: this.singleEventInformation.is_sales_open,
          eventCode: this.eventCode,
        },
      };
    },
    cardDisplay() {
      return (this.SET_IS.marketPlace)
        ? this.SET_MARKETPLACE.display.eventInformation.card
        : this.SET_DISPLAY.ticket.eventInformation.card;
    },
    isOnSale() {
      if (typeof this.singleEventInformation?.is_sales_open === 'boolean') { // eslint-disable-line complexity, camelcase
        return this.singleEventInformation.is_sales_open;
      }
      const startDate = this.singleEventInformation.sales_start;

      return date.getDateDiff(startDate, new Date(), 'seconds') < 0;
    },
    eventCode() {
      return (this.SET_EVENT_CODE) ? this.SET_EVENT_CODE : this.$route.params.code;
    },
    indicativeClassification() {
        return {
          displayOnWeb: this.singleEventInformation?.age_ratings?.display_on_web || false, // eslint-disable-line complexity, camelcase
          imageURL: this.singleEventInformation?.age_ratings?.image_url || '', // eslint-disable-line complexity, camelcase
          description: this.singleEventInformation?.age_ratings?.description || '', // eslint-disable-line complexity, camelcase
        };
      },
  },
  watch: {
    'singleEventInformation.name': {
      handler(newValue) {
        document.title = `${this.SET_PAGE_NAME} | ${newValue}`;
      },
    },
  },
  async beforeMount() {
    try {
      await this.fetchData();

      if (this.singleEventInformation.status !== 'Informativo') {
        if (this.isOnSale) {
          this.$router.replace({ name: TICKET_EVENT_NAME });
        } else {
          this.$router.replace({ name: TICKET_FUTURE_EVENT_NAME });
        }
      }

      if (this.isCartValid() || this.sessionCart) {
        await this.syncCart();
      } else {
        await this.synchronizeCart(true);
      }

      await this.syncItemsData();

      this.$tracker.pageView({
        page: this.$route.path,
        title: this.singleEventInformation.name,
      });
    } catch (error) {
      const parsedError = error || new Error(this.$t('page.event.error'));
      this.toastError(parsedError);
      if (parsedError.isAxiosError && (parsedError?.response?.status >= 400 || parsedError.status >= 400)) {
        this.$router.push({ name: ROUTER_CONSTANTS.EVENT_NOT_FOUND_NAME });
      }
    }
  },
  mounted() {
    if (!this.$q.platform.is.mobile) {
      LayoutHelpers.fixLeftPadding('q-layout-page-container');
    }
    this.$root.$on(UPDATE_PAGE_DATA, async () => {
      await this.fetchData();
      await this.syncItemsData();
    });
  },
  methods: {
    ...mapActions('ticketSale', [
      'getEventInformation',
    ]),
    ...mapActions('ticketShoppingCart', [
      'synchronizeCart',
      'addItemToCart',
    ]),
    syncCart() {
      return this.synchronizeCart()
        .catch((error) => {
          if (error.status >= 400) return this.resetCartAndGoToHome();
          const errorMessage = objectValidator(error, 'data.status_code') || objectValidator(error,
            'data.message');
          if (errorMessage) return this.toastError(errorMessage);
          return false;
        });
    },
    async syncItemsData() {
      await this.addItemToCart(JSON.parse(JSON.stringify(this.singleEventInformation)));
    },
    isInfoEvent(event) {
      return event.status === 'Informativo';
    },
    async fetchData() {
      try {
        return this.getEventInformation({ eventCode: this.eventCode });
      } catch (err) {
        this.toastError(err);
        return Promise.reject(err);
      }
    },
  },
};
</script>
